import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { BookOpen, Star, Clock, User, LogOut, MessageCircle, Play, Shield, Loader2, GraduationCap, ChevronDown, ChevronUp, Smartphone, Monitor } from "lucide-react";
import { toast } from "sonner";

/**
 * User Library Page
 * 
 * Personal dashboard showing:
 * - User's available books
 * - Reading progress
 * - Quick access to books
 * - Account management
 * - Navigation back to main site
 */

interface UserBook {
  id: string;
  title: string;
  subtitle: string;
  author: string;
  description: string;
  coverImage: string;
  bookUrl: string;
  access_type: "free" | "purchased";
  progress: number;
  last_read_at: string | null;
}

export default function Library() {
  const { user, logout, token, isAdmin } = useAuth();
  const [books, setBooks] = useState<UserBook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  const [showDesktopInstructions, setShowDesktopInstructions] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUserBooks();
    }
  }, [token]);

  const fetchUserBooks = async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("/api/auth/user-books", {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setBooks(data.books);
        // 🔥 WARMUP: Silently ping Neon so it's warm before user opens the book
        fetch("/api/book-access/analytics/active-session/warmup/wtbtg", {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {}); // Silently ignore any errors - this is just a warmup
      } else {
        console.error("Failed to load books:", data.error);
        toast.error("Failed to load your books");
      }
    } catch (error) {
      console.error("Error fetching user books:", error);
      toast.error("Failed to load your books");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartReading = async () => {
    if (!token) {
      toast.error("Please log in to start reading");
      return;
    }

    setIsGeneratingToken(true);

    try {
      const response = await fetch("/api/book-access/generate-token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          bookId: "wtbtg"
        }),
      });

      const data = await response.json();

      if (data.success && data.bookUrl) {
        // Navigate to the book using the generated token
        window.location.href = data.bookUrl;
      } else {
        console.error("Failed to generate book access token:", data.error);
        toast.error(data.error || "Failed to start reading. Please try again.");
      }
    } catch (error) {
      console.error("Error generating book access token:", error);
      toast.error("Failed to start reading. Please try again.");
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-forest-50">
      {/* Library Header - No marketing navigation */}
      <div className="bg-white border-b border-forest-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="w-10 h-10 bg-forest-100 rounded-full flex items-center justify-center"
              >
                <BookOpen className="w-5 h-5 text-forest-600" />
              </motion.div>
              <div>
                <h1 className="text-xl font-bold text-forest-900">My Library</h1>
                <p className="text-sm text-forest-600">Welcome back, {user?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {isAdmin && (
                <Button 
                  variant="outline" 
                  className="border-golden-200 text-golden-600 bg-golden-50 hover:bg-golden-70 hover:text-golden-800"
                  asChild
                >
                  <Link to="/admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Panel
                  </Link>
                </Button>
              )}

              {user?.role === "educator" && (
                <Button
                  variant="outline"
                  className="border-golden-200 text-golden-600 bg-golden-50 hover:bg-golden-70 hover:text-golden-800"
                  asChild
                >
                  <Link to="/educator">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Educator Dashboard
                  </Link>
                </Button>
              )}

              {(user?.role === "educator" || isAdmin) && (
                <Button
                  variant="outline"
                  className="border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                  asChild
                >
                  <Link to="/community">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Community
                  </Link>
                </Button>
              )}
              
              <Button 
                variant="outline" 
                className="border-forest-200 text-forest-600 bg-forest-50 hover:bg-forest-70 hover:text-forest-800"
                onClick={() => window.open("/feedback", "_blank", "noopener,noreferrer")}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Give Feedback
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-coral-200 text-coral-600 bg-coral-50 hover:bg-coral-70 hover:text-coral-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="border-forest-200 bg-white/95 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-golden-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-golden-600" />
                </div>
                <div>
                  <CardTitle className="text-forest-900">Welcome to Your Library</CardTitle>
                  <CardDescription className="text-forest-600">
                    Access your books, track your progress, and continue your reading journey.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-4 px-6">
              <div className="border-t border-forest-100 pt-4">
                <div className="grid grid-cols-2 gap-4">

                  {/* Desktop install */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-forest-500 flex-shrink-0" />
                        <p className="text-sm text-forest-700">
                          <strong>Install as a desktop app</strong>
                        </p>
                      </div>
                      <button
                        onClick={() => setShowDesktopInstructions(p => !p)}
                        className="flex items-center gap-1 text-xs text-forest-500 hover:text-forest-700 transition-colors ml-2 flex-shrink-0"
                      >
                        {showDesktopInstructions ? (
                          <><ChevronUp className="w-3.5 h-3.5" /> Hide</>
                        ) : (
                          <><ChevronDown className="w-3.5 h-3.5" /> How?</>
                        )}
                      </button>
                    </div>
                    {showDesktopInstructions && (
                      <div className="mt-3 space-y-2 text-sm text-forest-600 bg-forest-50 rounded-xl p-3">
                        <p className="flex items-start gap-2">
                          <span>🖥️</span>
                          <span><strong>Chrome or Edge:</strong> Look for the install icon (computer screen with down arrow) in your address bar → click it → "Install Dira Stories"</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span>📌</span>
                          <span><strong>To reopen:</strong> Search "Dira Stories" in Spotlight (Mac) or Start Menu (Windows), or pin it to your Dock / Taskbar</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Mobile install */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-forest-500 flex-shrink-0" />
                        <p className="text-sm text-forest-700">
                          <strong>Add to phone or tablet</strong>
                        </p>
                      </div>
                      <button
                        onClick={() => setShowInstallInstructions(p => !p)}
                        className="flex items-center gap-1 text-xs text-forest-500 hover:text-forest-700 transition-colors ml-2 flex-shrink-0"
                      >
                        {showInstallInstructions ? (
                          <><ChevronUp className="w-3.5 h-3.5" /> Hide</>
                        ) : (
                          <><ChevronDown className="w-3.5 h-3.5" /> How?</>
                        )}
                      </button>
                    </div>
                    {showInstallInstructions && (
                      <div className="mt-3 space-y-2 text-sm text-forest-600 bg-forest-50 rounded-xl p-3">
                        <p className="flex items-start gap-2">
                          <span>🍎</span>
                          <span><strong>iPhone / Safari:</strong> Tap the share button (box with arrow) → scroll down → "Add to Home Screen"</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span>🤖</span>
                          <span><strong>Android / Chrome:</strong> Tap the 3-dot menu → "Add to Home Screen" or "Install App"</span>
                        </p>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Books Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-forest-900 mb-2">Your Books</h2>
            <p className="text-forest-600">Click any book to start reading</p>
          </div>

          {/* Book Cards Grid */}
          <div className="grid gap-6 max-w-5xl mx-auto">
            {/* Where the Brave Things Grow - Compact Card matching the image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
            <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-green-100 via-green-50 to-white shadow-lg shadow-xl all duration-300 rounded-2xl">
              <CardContent className="p-8">
                {/* Top Badges */}
                <div className="flex items-center justify-between mb-6">
                  <Badge className="bg-golden-200 text-golden-800 border-0 px-4 py-2 text-sm font-medium rounded-full">
                    📖 Interactive Digital Book
                  </Badge>
                  <Badge className="bg-golden-200 text-golden-800 border-0 px-4 py-2 text-sm font-medium rounded-full">
                    Free
                  </Badge>
                </div>

                {/* Title */}
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-bold text-forest-800 mb-2">
                    Where the Brave Things Grow
                  </h2>
                </div>

                {/* Characters Row */}
                <div className="flex justify-center gap-6 mb-6">
                  <span className="text-3xl">🐿️</span>
                  <span className="text-3xl">🐭</span>
                  <span className="text-3xl">🐰</span>
                  <span className="text-3xl">🐢</span>
                </div>

                {/* Interactive Cues Row */}
                <div className="flex justify-center gap-4 mb-8">
                  <span className="text-2xl">🍃</span>
                  <span className="text-2xl">🌈</span>
                  <span className="text-2xl">🛡️</span>
                  <span className="text-2xl">✨</span>
                  <span className="text-2xl">💚</span>
                  <span className="text-2xl">💧</span>
                </div>

                {/* Divider Line */}
                <div className="w-full h-px bg-forest-200 mb-6"></div>

                {/* Title and Description */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-forest-800 mb-2">
                    Where the Brave Things Grow
                  </h3>
                  <p className="text-forest-600 mb-3">
                    Join Mila and friends on a magical forest adventure
                  </p>
                  <p className="text-forest-500 text-sm mb-4">
                    by Where the Brave Things Grow Team
                  </p>
                </div>

                {/* Bottom Row with Age Info and Button */}
                <div className="flex items-center justify-between">
                  <div className="text-forest-600 text-sm">
                    <span>Perfect for ages 3-8 • </span>
                    <span>Family reading</span>
                  </div>
                  
                  <Button
                    onClick={handleStartReading}
                    disabled={isGeneratingToken}
                    className="bg-forest-600 bg-forest-700 text-white px-6 py-3 rounded-full font-medium all duration-300 scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                  >
                    {isGeneratingToken ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Start Your Adventure
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

            {/* Where the Brave Things Teach (Book 2) - Admin Only */}
            {isAdmin && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-blue-100 via-blue-50 to-white shadow-lg shadow-xl all duration-300 rounded-2xl">
                  <CardContent className="p-8">
                    {/* Top Badges */}
                    <div className="flex items-center justify-between mb-6">
                      <Badge className="bg-blue-200 text-blue-800 border-0 px-4 py-2 text-sm font-medium rounded-full">
                        📖 Interactive Digital Book
                      </Badge>
                      <Badge className="bg-red-500 text-white border-0 px-4 py-2 text-sm font-medium rounded-full">
                        🔒 Admin Only
                      </Badge>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-forest-800 mb-2">
                        Where the Brave Things Teach
                      </h2>
                      <p className="text-sm text-blue-600 font-medium">Book 2 - Coming Soon</p>
                    </div>

                    {/* Characters Row */}
                    <div className="flex justify-center gap-6 mb-6">
                      <span className="text-3xl">👨‍🏫</span>
                      <span className="text-3xl">👩‍🏫</span>
                      <span className="text-3xl">📚</span>
                      <span className="text-3xl">🎓</span>
                    </div>

                    {/* Divider Line */}
                    <div className="w-full h-px bg-forest-200 mb-6"></div>

                    {/* Title and Description */}
                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-forest-800 mb-2">
                        Where the Brave Things Teach
                      </h3>
                      <p className="text-forest-600 mb-3">
                        A guide for educators bringing emotional learning to classrooms
                      </p>
                      <p className="text-forest-500 text-sm mb-4">
                        by Where the Brave Things Grow Team
                      </p>
                    </div>

                    {/* Bottom Row with Info and Button */}
                    <div className="flex items-center justify-between">
                      <div className="text-forest-600 text-sm">
                        <span>Teacher Resource Guide</span>
                      </div>
                      
                      <Button
                        onClick={() => toast.info("Book 2 is currently in development")}
                        className="bg-blue-600 bg-blue-700 text-white px-6 py-3 rounded-full font-medium all duration-300 scale-105"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Preview (Admin)
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
